import { User, Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { updateSite } from "@/lib/api";
import { formatPhoneNumber } from "@/lib/phoneFormat";

// Billing Contact UI for the customer side-sheet. Three branches:
//
//   1. SINGLE-SITE CUSTOMER → render 3 inputs inline; once filled,
//      collapse into a single click-to-edit row (matches the Contacts
//      section UX).
//
//   2. "ALL SITES" view → click-to-edit pattern at the client level.
//
//   3. SITE-SPECIFIC → "Use default billing contact" checkbox +
//      optional override fields stored on the site itself.
//
// Extracted from Clients.jsx (Feb 2026, iter176). All API
// side-effects (updateSite) and the `formLoadingRef` guard against
// auto-save recursion stay identical to the prior inline code.
export function BillingContactSection({
  selectedClient,
  formData, setFormData,
  clientSites, setClientSites,
  selectedSiteId,
  editingBillingContact, setEditingBillingContact,
  formLoadingRef,
}) {
  const currentSite = selectedSiteId !== "all"
    ? clientSites.find((s) => s.id === selectedSiteId)
    : null;

  const clientBilling = {
    name:  selectedClient?.billing_contact_name  || formData.billing_contact_name  || "",
    email: selectedClient?.billing_contact_email || formData.billing_contact_email || "",
    phone: selectedClient?.billing_contact_phone || formData.billing_contact_phone || "",
  };
  const hasSiteOverride = currentSite && currentSite.use_site_billing === true;

  // ---- BRANCH 1: SINGLE-SITE CUSTOMER ---------------------------------
  if (clientSites.length <= 1) {
    const hasData = !!(formData.billing_contact_name || formData.billing_contact_email || formData.billing_contact_phone);
    const isEditing = editingBillingContact || !hasData;

    // When the user types into the client-level billing fields, the
    // sole site's own billing should NOT shadow it. Strip any
    // use_site_billing override and clear the site-level contact
    // mirrors so the auto-save propagates the new client-level value
    // unambiguously.
    const normalizeSiteInherit = async () => {
      const sole = clientSites[0];
      if (!sole) return;
      if (sole.use_site_billing === true || sole.billing_contact_name || sole.billing_contact_email || sole.billing_contact_phone) {
        const updated = { ...sole, use_site_billing: false, billing_contact_name: "", billing_contact_email: "", billing_contact_phone: "" };
        setClientSites((prev) => prev.map((s) => s.id === sole.id ? updated : s));
        formLoadingRef.current = true;
        try {
          const { id, lat, lng, created_at, updated_at, ...p } = updated;
          await updateSite(sole.id, p);
        } catch { /* swallow — auto-save will pick it up next pass */ }
        setTimeout(() => { formLoadingRef.current = false; }, 100);
      }
    };

    if (!isEditing) {
      // Locked / display state — gray pill, 1 row, click to edit.
      return (
        <div
          className="flex items-center gap-2 p-2 rounded bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setEditingBillingContact(true)}
          data-testid="billing-contact-display"
        >
          <User className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0 text-sm flex items-center gap-2 flex-nowrap overflow-hidden">
            {formData.billing_contact_name && (
              <span className="font-medium truncate">{formData.billing_contact_name}</span>
            )}
            {formData.billing_contact_email && (
              <a
                href={`mailto:${formData.billing_contact_email}`}
                className="text-brand hover:underline text-xs truncate"
                onClick={(e) => e.stopPropagation()}
              >{formData.billing_contact_email}</a>
            )}
            {formData.billing_contact_phone && (
              <a
                href={`tel:${formData.billing_contact_phone.replace(/\D/g, '')}`}
                className="text-brand hover:underline text-xs whitespace-nowrap"
                onClick={(e) => e.stopPropagation()}
              >{formData.billing_contact_phone}</a>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 shrink-0 text-slate-300 hover:text-red-500"
            onClick={(e) => {
              e.stopPropagation();
              setFormData((prev) => ({ ...prev, billing_contact_name: "", billing_contact_email: "", billing_contact_phone: "" }));
              setEditingBillingContact(true);
            }}
            data-testid="btn-clear-billing-contact"
            title="Remove billing contact"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      );
    }

    // Edit state — 3 inputs + green "+ add" lock button.
    return (
      <div
        className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center [&>*:first-child]:col-span-3"
        data-testid="billing-contact-inputs"
      >
        <Input
          value={formData.billing_contact_name || ""}
          onChange={(e) => setFormData((prev) => ({ ...prev, billing_contact_name: e.target.value }))}
          onFocus={() => setEditingBillingContact(true)}
          onBlur={normalizeSiteInherit}
          placeholder="Name"
          className="h-8 text-sm"
          data-testid="input-client-billing-name"
        />
        <Input
          value={formData.billing_contact_email || ""}
          onChange={(e) => setFormData((prev) => ({ ...prev, billing_contact_email: e.target.value }))}
          onFocus={() => setEditingBillingContact(true)}
          onBlur={normalizeSiteInherit}
          placeholder="Email"
          className="h-8 text-sm"
          data-testid="input-client-billing-email"
        />
        <Input
          value={formData.billing_contact_phone || ""}
          onChange={(e) => setFormData((prev) => ({ ...prev, billing_contact_phone: formatPhoneNumber(e.target.value) }))}
          onFocus={() => setEditingBillingContact(true)}
          onBlur={normalizeSiteInherit}
          placeholder="(303) 333-3333"
          className="h-8 text-sm"
          data-testid="input-client-billing-phone"
        />
        <Button
          type="button"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={!hasData}
          onClick={() => setEditingBillingContact(false)}
          data-testid="btn-add-billing-contact"
          title="Add billing contact"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  // ---- BRANCH 2: "ALL SITES" VIEW -------------------------------------
  if (selectedSiteId === "all") {
    const hasData = !!(formData.billing_contact_name || formData.billing_contact_email || formData.billing_contact_phone);
    if (hasData && !editingBillingContact) {
      // Read-only display — click to edit
      return (
        <div
          className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setEditingBillingContact(true)}
          data-testid="billing-contact-display"
        >
          <User className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm flex items-center gap-2 flex-wrap">
              {formData.billing_contact_name && (
                <span className="font-medium">{formData.billing_contact_name}</span>
              )}
              {formData.billing_contact_email && (
                <a
                  href={`mailto:${formData.billing_contact_email}`}
                  className="text-brand hover:underline text-xs"
                  onClick={(e) => e.stopPropagation()}
                >{formData.billing_contact_email}</a>
              )}
            </div>
            {formData.billing_contact_phone && (
              <div className="text-xs text-slate-500">
                <a
                  href={`tel:${formData.billing_contact_phone.replace(/\D/g, '')}`}
                  className="text-brand hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >{formData.billing_contact_phone}</a>
              </div>
            )}
          </div>
        </div>
      );
    }
    // Editable inputs (empty state or clicked into)
    return (
      <div>
        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center [&>*:first-child]:col-span-3">
          <Input
            value={formData.billing_contact_name || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, billing_contact_name: e.target.value }))}
            placeholder="Name"
            className="h-8 text-sm"
            data-testid="input-client-billing-name"
          />
          <Input
            value={formData.billing_contact_email || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, billing_contact_email: e.target.value }))}
            placeholder="Email"
            className="h-8 text-sm"
            data-testid="input-client-billing-email"
          />
          <Input
            value={formData.billing_contact_phone || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, billing_contact_phone: formatPhoneNumber(e.target.value) }))}
            placeholder="(303) 333-3333"
            className="h-8 text-sm"
            data-testid="input-client-billing-phone"
          />
          {hasData && (
            <Button
              type="button"
              size="sm"
              className="h-8"
              onClick={() => setEditingBillingContact(false)}
              data-testid="btn-done-billing-edit"
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ---- BRANCH 3: SITE-SPECIFIC --------------------------------------
  // "Use default billing contact" checkbox + optional override fields.
  // The override fields are stored on the site record itself
  // (site.billing_contact_*) so each site can have its own AP contact.

  // Push the site-record changes to the server. The formLoadingRef
  // guard prevents the customer-level auto-save effect from re-firing
  // mid-write — see the matching guard in the parent's auto-save
  // useEffect.
  const persistSiteEdit = async (updated) => {
    setClientSites((prev) => prev.map((s) => s.id === updated.id ? updated : s));
    formLoadingRef.current = true;
    try {
      const { id, lat, lng, created_at, updated_at, ...sitePayload } = updated;
      await updateSite(updated.id, sitePayload);
    } catch { /* swallow */ }
    setTimeout(() => { formLoadingRef.current = false; }, 100);
  };

  return (
    <div className="space-y-2">
      <label
        className="flex items-center gap-2 text-xs cursor-pointer"
        data-testid="use-default-billing-toggle"
      >
        <input
          type="checkbox"
          checked={!hasSiteOverride}
          onChange={async (e) => {
            const useDefault = e.target.checked;
            const updated = { ...currentSite, use_site_billing: !useDefault };
            if (useDefault) {
              updated.billing_contact_name = "";
              updated.billing_contact_email = "";
              updated.billing_contact_phone = "";
            }
            await persistSiteEdit(updated);
          }}
          className="h-3.5 w-3.5 accent-blue-600 rounded"
        />
        <span className="font-medium">Use default billing contact</span>
      </label>

      {!hasSiteOverride ? (
        // Show inherited (read-only display)
        clientBilling.name || clientBilling.email ? (
          <div className="text-xs text-slate-600 bg-slate-50 rounded px-3 py-2 flex items-center gap-4 flex-wrap">
            {clientBilling.name  && <span className="font-medium">{clientBilling.name}</span>}
            {clientBilling.email && <a href={`mailto:${clientBilling.email}`} className="text-brand hover:underline">{clientBilling.email}</a>}
            {clientBilling.phone && <a href={`tel:${clientBilling.phone.replace(/\D/g, '')}`} className="text-brand hover:underline">{clientBilling.phone}</a>}
          </div>
        ) : (
          <div className="text-[10px] text-slate-400 italic">
            No default billing contact set — configure under &quot;All Sites&quot;
          </div>
        )
      ) : (
        // Site-specific override fields
        <div className="grid grid-cols-2 gap-2 [&>*:first-child]:col-span-2">
          <Input
            value={currentSite?.billing_contact_name || ""}
            onChange={(e) => setClientSites((prev) => prev.map((s) => s.id === currentSite.id ? { ...s, billing_contact_name: e.target.value } : s))}
            onBlur={async () => {
              const cs = clientSites.find((s) => s.id === selectedSiteId);
              if (cs) await persistSiteEdit(cs);
            }}
            placeholder="Name"
            className="h-8 text-sm"
            data-testid="input-site-billing-name"
          />
          <Input
            value={currentSite?.billing_contact_email || ""}
            onChange={(e) => setClientSites((prev) => prev.map((s) => s.id === currentSite.id ? { ...s, billing_contact_email: e.target.value } : s))}
            onBlur={async () => {
              const cs = clientSites.find((s) => s.id === selectedSiteId);
              if (cs) await persistSiteEdit(cs);
            }}
            placeholder="Email"
            className="h-8 text-sm"
            data-testid="input-site-billing-email"
          />
          <Input
            value={currentSite?.billing_contact_phone || ""}
            onChange={(e) => {
              const val = formatPhoneNumber(e.target.value);
              setClientSites((prev) => prev.map((s) => s.id === currentSite.id ? { ...s, billing_contact_phone: val } : s));
            }}
            onBlur={async () => {
              const cs = clientSites.find((s) => s.id === selectedSiteId);
              if (cs) await persistSiteEdit(cs);
            }}
            placeholder="(303) 333-3333"
            className="h-8 text-sm"
            data-testid="input-site-billing-phone"
          />
        </div>
      )}
    </div>
  );
}
